/*

# BUILDER

This controls the overall state and the wizard actions
of moving forward, going backwards, or jumping to a step

The builder can know about the steps, but the steps can't know about the builder.
That way the child events are only ever subscribed to by the parent (this) so it
should make it a bit easier to reason about.

# STEPS

1. Note
  a. New note
  b. Existing note
2. Terms
3. Portfolio
  a. Model
  b. Self select
4. Strategy
5. Complete

*/

import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit'
import { max } from 'lodash'
import { all, put, select, takeEvery } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import { ApiAccountDocumentationInfo } from '@/api'
import api from '@/api/_legacy'
import {
  ApiAccountApprovalEvent,
  ApiAccountApprovalState,
  ApiAccountBuilderState,
  ApiAccountSnapshot,
  ApiAccountState,
  ApiAccountStatus,
  ApiBusinessUnit,
  ApiClient,
  ApiNote,
  ApiTradingAccount,
  UserInformation
} from '@/api/_legacy/generated-api'
import { accountStatusToStep } from '@/types/tradeApproval'
import { UserRole, UserType } from '@/types/user'
import { Currency } from '@/utilities/currency'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { mapToTradingAccount } from '../../../../draft-trades/state/draft-trades-slice'
import { TradingAccount } from '../../../../draft-trades/types'
import { actions as topupWithdrawActions } from '../../../../trade-editor/modules/01-topup-withdraw/state/topup-withdraw-slice'
import { SavedState, TradeActivationStep } from '../../../types'
import { Errors, extractErrorMessagesFromServerResponse } from '../../../util'
import { actions as existingNoteActions } from '../../01-note/existing-note/existing-note-slice'
import { actions as noteActions, NoteStepMoveNextPayload } from '../../01-note/note/note-step-slice'
import { actions as termsActions } from '../../02-terms/state/terms-slice'
import { actions as portfolioActions } from '../../03-portfolio/state/portfolio-slice'
import { actions as strategyActions } from '../../04-strategy/state/strategy-slice'
import { closeAccountSaga, closeAllAccountsSaga } from './close-account'
import { saveAccountSaga, updateAccountSaga } from './save-account'
import { submitAccountSaga } from './submit-account'

export const TENANT_ID = 'absa'

export type StepName = 'note' | 'terms' | 'portfolio' | 'strategy' | 'complete' | 'topupWithdraw'

export type TradeApprovalEvent = {
  comment?: string
  firstName: string
  lastName: string
  organisation: string
  statusAfter: string
  statusBefore: string
  time: string
  userId: string
}

export type TradeApprovalNotification = {
  description?: string
  title: string
}

export type TradeApprovalUpdate = {
  approvalNotification: TradeApprovalNotification
  nextStatus: TradeActivationStep
}

type AccountWithSnapshot = {
  account: ApiTradingAccount
  documentationSummary?: ApiAccountDocumentationInfo[]
  latestSnapshot?: ApiAccountSnapshot
}

export type BuilderInitialiseState = {
  accountId?: string
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export type WizardState = {
  accountId?: string
  approvalEvents?: ApiAccountApprovalEvent[]
  approvalNotification?: TradeApprovalNotification
  businessUnit?: ApiBusinessUnit
  canProceed: boolean
  client?: ApiClient
  completeKey: string
  currency: Currency
  errors: Errors
  isFinished: boolean
  note?: ApiNote
  noteKey: string
  portfolioKey: string
  previousStep?: StepName
  requiresFullApproval: boolean
  savedState?: SavedState
  selectedAccount?: TradingAccount
  selectedAccountId: string
  status: TradeActivationStep
  step: StepName
  strategyKey: string
  termsKey: string
  updatedAccountPendingState: ApiAccountBuilderState
  userName: string
  userRoles: UserRole[]
  userType: UserType
  usersInformation: UserInformation[]
}

const initialState: WizardState = {
  step: 'note',
  canProceed: false,
  noteKey: nanoid(),
  termsKey: nanoid(),
  portfolioKey: nanoid(),
  strategyKey: nanoid(),
  completeKey: nanoid(),
  isFinished: false,
  userType: 'admin',
  userRoles: [],
  userName: undefined,
  usersInformation: [],
  selectedAccountId: undefined,
  currency: undefined,
  status: undefined,
  approvalNotification: undefined,
  selectedAccount: undefined,
  errors: {},
  requiresFullApproval: true,
  updatedAccountPendingState: undefined
}

export const builderSlice = createSlice({
  name: 'builder',
  initialState,
  reducers: {
    initNewAccount: state => {
      state.step = 'note'
      state.noteKey = nanoid()
      state.termsKey = nanoid()
      state.portfolioKey = nanoid()
      state.strategyKey = nanoid()
    },
    setUserType: (state, action: PayloadAction<UserType>) => {
      state.userType = action.payload
    },
    setUserName: (state, action: PayloadAction<string>) => {
      state.userName = action.payload
    },
    setUserRoles: (state, action: PayloadAction<UserRole[]>) => {
      state.userRoles = action.payload
    },
    setSavedState: (state, action: PayloadAction<SavedState>) => {
      state.errors = undefined
      state.selectedAccount = mapToTradingAccount(action.payload.account)
      state.savedState = action.payload
      state.status = action.payload.status
      state.currency = action.payload.currency as Currency
      if (
        action.payload.account.pendingAccountState &&
        action.payload.account.pendingAccountState.updateDoesNotRequireFullApproval !== undefined
      ) {
        state.requiresFullApproval =
          !action.payload.account.pendingAccountState.updateDoesNotRequireFullApproval
      }
    },
    resetSavedState: state => {
      state.selectedAccount = undefined
      state.savedState = undefined
      state.errors = undefined
    },
    setNoteDetails: (state, action: PayloadAction<NoteStepMoveNextPayload>) => {
      state.client = action.payload.client
      state.businessUnit = action.payload.businessUnit
      state.note = action.payload.note
    },
    setAccountId: (state, action: PayloadAction<string>) => {
      state.accountId = action.payload
      state.selectedAccountId = action.payload
    },
    setApprovalState: (state, action: PayloadAction<ApiAccountApprovalState>) => {
      const approvalState = action.payload
      // https://dev.azure.com/fnz-qhub/Q-Hub/_workitems/edit/2225/
      // Filter out events from previous activation process, only include from last time trade moved from active to draft
      const activeStatusEvents = approvalState.approvalEvents.filter(
        item =>
          item.statusBefore &&
          item.statusBefore === 'Active' &&
          item.statusAfter &&
          item.statusAfter === 'Draft'
      )

      if (activeStatusEvents.length > 0) {
        const lastActiveStateIndex = approvalState.approvalEvents.indexOf(
          activeStatusEvents[activeStatusEvents.length - 1]
        )
        const inScopeEvents = approvalState.approvalEvents.slice(lastActiveStateIndex + 1)
        state.approvalEvents = inScopeEvents
      } else {
        state.approvalEvents = approvalState.approvalEvents
      }
    },
    moveBackToNote: state => {
      state.step = 'note'
    },
    moveBackToTerms: state => {
      state.step = 'terms'
    },
    moveBackToPortfolio: state => {
      state.step = 'portfolio'
    },
    moveBackToStrategy: state => {
      state.step = 'strategy'
    },
    moveToTerms: state => {
      state.step = 'terms'
      state.termsKey = nanoid()
    },
    moveToPortfolio: state => {
      state.errors = {}
      state.step = 'portfolio'
      state.portfolioKey = nanoid()
    },
    moveToStrategy: state => {
      state.errors = {}
      state.step = 'strategy'
      state.strategyKey = nanoid()
    },
    moveToCompletePostUpdate: (state, action: PayloadAction<ApiTradingAccount>) => {
      state.errors = {}
      state.step = 'complete'
      state.completeKey = nanoid()
      state.updatedAccountPendingState = action.payload.pendingAccountState
    },
    moveToComplete: state => {
      state.errors = {}
      state.step = 'complete'
      state.completeKey = nanoid()
    },
    moveToFinished: state => {
      state.isFinished = true
    },
    goToStep: (state, action: PayloadAction<StepName>) => {
      state.step = action.payload
    },
    goBackFromCompleteToStep: (state, action: PayloadAction<StepName>) => {
      state.previousStep = 'complete'
      state.step = action.payload
    },
    moveToPreviousNavigatedStep: state => {
      if (state.previousStep) {
        state.errors = {}
        state.step = state.previousStep
        state.previousStep = undefined
      }
    },
    closeTrade: () => {
      // dummy comment to keep linter happy
    },
    closeAllTradesInNote: () => {
      // dummy comment to keep linter happy
    },
    saveError: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setApprovalNotification: (state, action: PayloadAction<TradeApprovalNotification>) => {
      state.approvalNotification = action.payload
    },
    resetNotification: state => {
      state.approvalNotification = undefined
    },
    addUsersToCache: (state, action: PayloadAction<UserInformation[]>) => {
      state.usersInformation = action.payload
    },
    setSelectedAccountId: (state, action: PayloadAction<string>) => {
      state.selectedAccountId = action.payload
    },
    reset: () => initialState
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

const determineStep = (savedState: SavedState): StepName => {
  switch (true) {
    case !!savedState.account.pendingAccountState.strategy: {
      return 'strategy'
    }
    case !!savedState.account.pendingAccountState.portfolio: {
      return 'portfolio'
    }
    default: {
      return 'terms'
    }
  }
}

const getTradeDocumentationUrlFromStatus = (
  accountId: string,
  status: ApiAccountStatus,
  documentationSummary: ApiAccountDocumentationInfo[]
): string => {
  let tradeDocumentationUrl = ''
  let confirmationNumbers = []
  switch (status) {
    case 'PendingEconomicsApproval':
      confirmationNumbers = documentationSummary
        .filter(confirmation => confirmation.stage === 'Draft')
        .map(confirmation => confirmation.number)
      break
    case 'PendingApproval':
      confirmationNumbers = documentationSummary
        .filter(confirmation => confirmation.stage === 'PendingApproval')
        .map(confirmation => confirmation.number)
      break
    case 'PendingCommitment':
      confirmationNumbers = documentationSummary
        .filter(confirmation => confirmation.stage === 'PendingExecution')
        .map(confirmation => confirmation.number)
      break
    case 'PendingClientCommitment':
      confirmationNumbers = documentationSummary
        .filter(confirmation => confirmation.stage === 'PendingExecution')
        .map(confirmation => confirmation.number)
      break
    default:
      confirmationNumbers = documentationSummary
        .filter(confirmation => confirmation.stage === 'PendingExecution')
        .map(confirmation => confirmation.number)
  }
  tradeDocumentationUrl = `/documentation/account/${accountId}/${max(confirmationNumbers)}`
  return tradeDocumentationUrl
}

const loadState = (accountWithSnapshot: AccountWithSnapshot) => async dispatch => {
  const { account } = accountWithSnapshot
  const latestSnapshotAccountValue = accountWithSnapshot.latestSnapshot
    ? accountWithSnapshot.latestSnapshot.accountValue
    : 0
  const { approvalState, businessUnit, client, currentAccountState, note } = account
  const { portfolio, strategy } = currentAccountState || ({} as ApiAccountState)
  const savedState: SavedState = {
    account,
    note,
    currentPortfolio: portfolio,
    currentStrategy: strategy,
    client,
    businessUnit,
    currency: account.currency,
    pendingStateInvestment: account.pendingAccountState?.deposit,
    currentStateInvestment: account.currentAccountState?.investment,
    currentAccountValue: latestSnapshotAccountValue,
    status: accountStatusToStep(account.status),
    tradeDocumentationUrl: getTradeDocumentationUrlFromStatus(
      account.accountId,
      account.status,
      accountWithSnapshot.documentationSummary
    )
  }

  // builder
  dispatch(builderSlice.actions.setSavedState(savedState))
  dispatch(builderSlice.actions.setNoteDetails({ note, client, businessUnit }))
  dispatch(builderSlice.actions.setAccountId(account.accountId))
  dispatch(builderSlice.actions.setApprovalState(approvalState))

  // note
  dispatch(noteActions.loadState(savedState))
  dispatch(existingNoteActions.loadState(savedState))

  // topup
  dispatch(topupWithdrawActions.loadState(savedState))

  // terms
  dispatch(termsActions.loadState(savedState))

  // portfolio
  dispatch(portfolioActions.loadState(savedState))

  // strategy
  dispatch(strategyActions.loadState(savedState))

  const step = determineStep(savedState)
  dispatch(builderSlice.actions.goToStep(step))
}

const fetchAccountWithSnapshot = async (accountId: string) => {
  const account = await api.tenant.inprogressDetail2(accountId, TENANT_ID)
  let accountLatestSnapshot: ApiAccountSnapshot
  let documentationSummary: ApiAccountDocumentationInfo[]
  try {
    const documentationSummaryResponse = await api.tenant.accountSummaryDetail(accountId, TENANT_ID)
    documentationSummary = documentationSummaryResponse.data
  } catch {
    // ignore if documentation fails
  }

  // get lastest snapshot to get balance for live account
  if (account.data.newAccount === false) {
    try {
      const accountSnapshot = await api.tenant.snapshotsLatestDetail(
        'Internal',
        accountId,
        TENANT_ID
      )
      accountLatestSnapshot = accountSnapshot.data
    } catch {
      // ignore if snaptho fails
    }
  }

  return { account: account.data, latestSnapshot: accountLatestSnapshot, documentationSummary }
}

const loadExistingAccount = (accountId: string) => async dispatch => {
  dispatch(builderSlice.actions.setSelectedAccountId(accountId))
  const account = await fetchAccountWithSnapshot(accountId)
  dispatch(loadState(account))
}

const refreshTradeStatus =
  (accountId: string, approvalNotification: TradeApprovalNotification = undefined) =>
  async dispatch => {
    dispatch(builderSlice.actions.setApprovalNotification(approvalNotification))
    const account = await fetchAccountWithSnapshot(accountId)
    dispatch(loadState(account))
  }

const init = (state: BuilderInitialiseState) => dispatch => {
  dispatch(builderSlice.actions.setUserType(state.userType))
  dispatch(builderSlice.actions.setUserName(state.userName))
  dispatch(builderSlice.actions.setUserRoles(state.userRoles))
  if (!state.accountId) {
    dispatch(builderSlice.actions.initNewAccount())
    return
  }
  dispatch(loadExistingAccount(state.accountId))
}

export const actions = {
  ...builderSlice.actions,
  init,
  loadState,
  loadExistingAccount,
  refreshTradeStatus
}

export function* moveToTermsSaga(action: PayloadAction<NoteStepMoveNextPayload>): any {
  yield put(actions.setNoteDetails(action.payload))
  const state: RootState = yield select()
  if (state.builder.previousStep) {
    yield put(actions.moveToPreviousNavigatedStep())
  } else {
    yield put(actions.moveToTerms())
  }
}

export function* moveToPortfolioSaga(): any {
  const state: RootState = yield select()
  if (state.builder.previousStep) {
    yield put(actions.moveToPreviousNavigatedStep())
  } else {
    yield put(actions.moveToPortfolio())
  }
}

export function* moveToStrategySaga(): any {
  yield put(actions.moveToStrategy())
}

function* moveBackToNoteSaga(): any {
  yield put(actions.moveBackToNote())
}

function* moveBackToTermsSaga(): any {
  yield put(actions.moveBackToTerms())
}

function* moveBackToPortfolioSaga(): any {
  yield put(actions.moveBackToPortfolio())
}

function* moveBackToStrategySaga(): any {
  yield put(actions.moveBackToStrategy())
}

export function* builderRootSaga() {
  yield all([
    takeEvery('noteStep/moveNext', moveToTermsSaga),
    takeEvery('termsStep/moveNext', moveToPortfolioSaga),
    takeEvery(
      'topupWithdrawStep/updateAndMoveNext',
      updateAccountSaga(
        () => put(actions.moveToPortfolio()),
        e => put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'portfolioStep/moveNext',
      saveAccountSaga(put(actions.moveToStrategy()), e =>
        put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'portfolioStep/updateAndMoveNext',
      updateAccountSaga(
        () => put(actions.moveToStrategy()),
        e => put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'strategyStep/moveNext',
      saveAccountSaga(put(actions.moveToComplete()), e =>
        put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'strategyStep/updateAndMoveNext',
      updateAccountSaga(
        payload => put(actions.moveToCompletePostUpdate(payload)),
        e => put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'completeStep/finish',
      submitAccountSaga(put(actions.moveToFinished()), e =>
        put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery('termsStep/moveBack', moveBackToNoteSaga),
    takeEvery('portfolioStep/moveBack', moveBackToTermsSaga),
    takeEvery('strategyStep/moveBack', moveBackToPortfolioSaga),
    takeEvery('completeStep/moveBack', moveBackToStrategySaga),
    takeEvery(
      'builder/closeTrade',
      closeAccountSaga(put(actions.moveToFinished()), e =>
        put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    ),
    takeEvery(
      'builder/closeAllTradesInNote',
      closeAllAccountsSaga(put(actions.moveToFinished()), e =>
        put(actions.saveError(extractErrorMessagesFromServerResponse(e)))
      )
    )
  ])
}

export default builderSlice.reducer
